import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { useCountdown } from "../../hooks/useCountdown";
import { openGiftViewer } from "../../../giftsApi";

// Private Drops V2 Part A - a real, live chat-visible card for a scheduled
// gift, rendered by MessageBubble.tsx in place of a normal bubble whenever
// message.card.type === "private_drop". Reads the gift LIVE from the same
// global appState.gifts store every other drop surface (PrivateDropCard.tsx,
// PrivateDropLockedPage.tsx) already reads - GiftsScreen.tsx's GiftsBody is
// unconditionally mounted app-wide and owns every gift WS handler, so this
// card gets every future state change (unlock, reaction, cancel, edit, open
// together) for free, with zero new wiring here. Tap semantics mirror
// PrivateDropCard.tsx exactly - no second reveal flow.
export function PrivateDropChatCard({ giftId }: { giftId: string }) {
  const navigate = useNavigate();
  const gift = useAppState((s) => s.gifts.find((g) => g.id === giftId));
  const giftsLoaded = useAppState((s) => s.giftsLoaded);
  const countdown = useCountdown(gift?.locked ? gift.unlockAt : null);

  if (!gift) {
    // giftsLoaded distinguishes "hasn't fetched yet" (brief, boot-time only
    // - GiftsBody's own loadGiftsData() call is unconditional and app-wide)
    // from "genuinely gone" - a cancelled drop is filtered out of the array
    // server-side (GiftsDB.getForUser), not merely absent while loading.
    if (!giftsLoaded) {
      return (
        <div className="chat-drop-card is-loading" aria-hidden="true">
          <span className="chat-drop-card-icon">
            <i className="bi bi-gift-fill" />
          </span>
          <span className="chat-drop-card-body">
            <span className="chat-drop-card-label">Private Drop</span>
          </span>
        </div>
      );
    }
    return (
      <div className="chat-drop-card is-cancelled">
        <span className="chat-drop-card-icon" aria-hidden="true">
          <i className="bi bi-x-circle" />
        </span>
        <span className="chat-drop-card-body">
          <span className="chat-drop-card-label">Private Drop cancelled</span>
        </span>
      </div>
    );
  }

  const isSender = gift.direction === "sent";
  const isOpened = !!gift.viewedAt;

  function handleTap(): void {
    if (gift!.locked) {
      navigate(`/gifts/drop/${gift!.id}`);
      return;
    }
    // The sender can never fetch their own sent gift's media (server-
    // enforced, GET /api/gifts/:id/media is recipient-only) - same silent
    // no-op a sent GiftCard row's tap already is today, not a new
    // restriction introduced here.
    if (isSender) return;
    openGiftViewer(gift!.id);
  }

  return (
    <button
      type="button"
      className={`chat-drop-card${gift.locked ? " is-locked" : isOpened ? " is-opened" : " is-ready"}`}
      onClick={handleTap}
      aria-label={
        gift.locked ? `Private Drop, unlocks in ${countdown.label}` : isOpened ? "Private Drop, opened" : "Open Private Drop"
      }
    >
      <span className="chat-drop-card-icon" aria-hidden="true">
        <i className={`bi ${gift.locked ? "bi-lock-fill" : "bi-gift-fill"}`} />
      </span>
      <span className="chat-drop-card-body">
        <span className="chat-drop-card-label">{isSender ? "You sent a Private Drop" : "Private Drop"}</span>
        <span className="chat-drop-card-status">
          {gift.locked
            ? `Unlocks in ${countdown.label}`
            : isOpened
              ? isSender
                ? "Opened"
                : "Opened - tap to view"
              : isSender
                ? "Ready for them to open"
                : "Ready to open"}
        </span>
      </span>
      {!gift.locked && !isOpened && <span className="chat-drop-card-pulse" aria-hidden="true" />}
    </button>
  );
}
